import { create } from "zustand";
import { useSidebarStore } from "@/stores/sidebar";
import { markFelixSession } from "@/lib/uxBehavior";

// FB-0037: Default + min dropped so Felix doesn't over-squeeze the main
// content on typical 1280–1440px laptops. Users can still drag wider.
const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 360;
const MAX_WIDTH = 900;
const STORAGE_KEY = "felix_panel_width";

// FB-0037: Felix panel should never exceed ~40% of the viewport, even if
// the user persisted a larger width on a bigger monitor. Computes at
// load + ResizeObserver keeps it honest on window resize.
function maxAllowedForViewport(): number {
  if (typeof window === "undefined") return MAX_WIDTH;
  const viewport = window.innerWidth || 1440;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.floor(viewport * 0.4)));
}

function loadWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const w = parseInt(stored, 10);
      const cap = maxAllowedForViewport();
      if (w >= MIN_WIDTH && w <= cap) return w;
      if (w > cap) return cap;
    }
  } catch { /* ignore */ }
  return Math.min(DEFAULT_WIDTH, maxAllowedForViewport());
}

interface FelixPanelState {
  /** Whether the panel is open (single source of truth for both desktop and mobile) */
  open: boolean;
  width: number;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  setWidth: (width: number) => void;
}

export const useFelixPanelStore = create<FelixPanelState>((set) => ({
  open: false,
  width: loadWidth(),
  toggle: () =>
    set((s) => {
      const next = !s.open;
      // Sync felixMode with sidebar store
      useSidebarStore.getState().setFelixMode(next);
      // #589 — tag this session as Felix-engaged for cohort analysis.
      if (next) markFelixSession();
      return { open: next };
    }),
  setOpen: (open) => {
    useSidebarStore.getState().setFelixMode(open);
    if (open) markFelixSession();
    set({ open });
  },
  setWidth: (width) => {
    const cap = Math.min(MAX_WIDTH, maxAllowedForViewport());
    const clamped = Math.max(MIN_WIDTH, Math.min(cap, width));
    try { localStorage.setItem(STORAGE_KEY, String(clamped)); } catch { /* ignore */ }
    set({ width: clamped });
  },
}));

// FB-0037: On viewport resize, re-clamp the stored width so a wide
// panel on a big monitor doesn't crush the content area when a user
// docks their window or switches to a smaller screen.
//
// FB-0105 / FB-0127 follow-up (2026-05-04, Claire's HumanOS session):
// the sidebar's collapsed/expanded state shifts `--sidebar-width` by
// 232px without firing a window `resize` event. If the user expands
// the sidebar mid-session, the persisted Felix width can suddenly
// exceed available space and the panel clips on the right edge.
// Subscribe to the sidebar store so any `collapsed` flip re-runs the
// same clamp the resize handler would.
if (typeof window !== "undefined") {
  let rafId: number | null = null;
  const reclamp = () => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      const state = useFelixPanelStore.getState();
      const cap = maxAllowedForViewport();
      if (state.width > cap) state.setWidth(cap);
    });
  };
  window.addEventListener("resize", reclamp);
  // Re-clamp when the sidebar toggles. Tracking the boolean directly
  // means the subscription fires only on actual flips, not on every
  // sidebar-store mutation.
  // Defensive: in some test environments useSidebarStore is mocked
  // without getState/subscribe. Skip the wiring there — the resize
  // listener still covers the common case.
  try {
    if (
      typeof useSidebarStore.getState === "function" &&
      typeof useSidebarStore.subscribe === "function"
    ) {
      let prevCollapsed = useSidebarStore.getState().collapsed;
      useSidebarStore.subscribe((s) => {
        if (s.collapsed !== prevCollapsed) {
          prevCollapsed = s.collapsed;
          reclamp();
        }
      });
    }
  } catch { /* ignore — sidebar store unavailable in test env */ }
}

export { MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH };
